import React from "react";
import { Alert, Card, Row, Col, Typography, Space, Modal, Collapse } from "antd";
import {
    PlusOutlined,
    MinusOutlined,
    SwapOutlined,
    ClockCircleOutlined,
    SyncOutlined,
    PauseCircleOutlined,
    PlayCircleOutlined,
    CalendarOutlined,
    StopOutlined,
    EditOutlined,
    CopyOutlined,
    DeleteOutlined,
    UserOutlined,
    TeamOutlined
} from "@ant-design/icons";
import Incorporation from "../avenantsModalComponents/Incorporation";
import RetraitVehicule from "../avenantsModalComponents/RetraitVehicule";
import AnnulationEngins from "../avenantsModalComponents/AnnulationEngins";
import AugmentationDeGarantie from "../avenantsModalComponents/AugmentationDeGarantie";
import DiminutionDeGarantie from "../avenantsModalComponents/DiminutionDeGarantie";
import Prorogation from "../avenantsModalComponents/Prorogation";
import SuspensionContrat from "../avenantsModalComponents/SuspensionContrat";
import RemiseEnVigueur from "../avenantsModalComponents/RemiseEnVigueur";
import ReportEffet from "../avenantsModalComponents/ReportEffet";
import TransfertDeGaranties from "../avenantsModalComponents/TransfertDeGaranties";
import ChangementDeVehicule from "../avenantsModalComponents/ChangementDeVehicule";
import Renouvellement from "../avenantsModalComponents/Renouvellement";
import AvenantDOrdre from "../avenantsModalComponents/AvenantDOrdre";
import AvenantDOrdreEngin from "../avenantsModalComponents/AvenantDOrdreEngin";
import Duplicata from "../avenantsModalComponents/Duplicata";
import Resiliation from "../avenantsModalComponents/Resiliation";
import ChangementProducteur from "../avenantsModalComponents/ChangementProducteur";
import ChangementIntermediaire from "../avenantsModalComponents/ChangementIntermediaire";
import { toast } from "react-toastify";
import { isAfterToday, isWithinDaysAfter } from "../../../../variables/datesFunctions";
import { frenchDate } from "../../../../variables/functions";
import { LOGGED_USER } from "../../../../variables/constants";
import { rightsService } from "../../../../services/rightsService";

const { Title, Text } = Typography;

const AvenantsTab = ({
    contrat,
    reglements,
    // Rafraîchit le contrat affiché (DetailsContrat) sans recharger la page.
    // À appeler après un avenant « en place » dont le codeId ne change pas.
    onRefresh,
    // Ouvre la confirmation d'annulation DÉFINITIVE du contrat (DetailsContrat) —
    // réservée à un contrat VALIDÉ dont la date d'effet est encore future.
    onAnnulerContrat,
    modalIncorporation,
    modalRetrait,
    modalAugmentation,
    modalDiminution,
    modalProrogation,
    modalSuspension,
    modalRemiseEnVigueur,
    modalReportEffet,
    modalTransfertGarantie,
    modalChangementVehicule,
    modalRenouvellement,
    modalAvenantOrdre,
    modalAvenantOrdreEngin,
    modalDuplicata,
    modalResiliation,
    modalChangementProducteur,
    updateIncorporationModal,
    updateRetraitModal,
    updateAugmentationModal,
    updateDiminutionModal,
    updateProrogationModal,
    updateSuspensionModal,
    updateRemiseEnVigueurModal,
    updateReportEffetModal,
    updateTransfertGarantieModal,
    updateChangementVehiculeModal,
    updateRenouvellementModal,
    updateAvenantOrdreModal,
    updateAvenantOrdreEnginModal,
    updateDuplicataModal,
    updateResiliationModal,
    updateChangementProducteurModal,
}) => {
    // État local : la modale ne sert qu'ici (pas de remontée dans DetailsContrat).
    const [modalChangementIntermediaire, setModalChangementIntermediaire] = React.useState(false);
    const [modalAnnulationEngins, setModalAnnulationEngins] = React.useState(false);

    const primaryColor = "#1890ff";
    const secondaryColor = "#242166";
    const successColor = "#52c41a";
    const warningColor = "#faad14";
    const dangerColor = "#ff4d4f";
    const infoColor = "#13c2c2";

    const isUserConnected = !!LOGGED_USER;
    const isAdmin =
        LOGGED_USER?.agence?.typeAgence === 'SIEGE' ||
        LOGGED_USER?.agence == null;

    const avenantCardStyle = {
        borderRadius: "12px",
        height: "100%",
        transition: "all 0.3s ease",
        border: `1px solid ${secondaryColor}15`,
        boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
        cursor: "pointer"
    };

    const headerStyle = {
        background: `linear-gradient(135deg, ${secondaryColor} 0%, ${primaryColor} 100%)`,
        color: "white",
        padding: "16px 20px",
        borderRadius: "12px 12px 0 0",
        minHeight: "80px",
        display: "flex",
        alignItems: "center"
    };

    const buttonStyle = {
        borderRadius: "8px",
        height: "40px",
        fontWeight: 500,
        boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
        width: "100%",
        marginTop: "auto"
    };

    const withUserCheck = (action) => () => {
        if (!isUserConnected) {
            toast.error("Votre session a expiré. Veuillez vous reconnecter pour gérer les avenants.");
            return;
        }
        // Le siège peut intervenir pour toute agence, les autres conservent le comportement existant
        action();
    };

    const AvenantCard = ({ title, description, icon, color, onClick, badge, disabled = false }) => (
        <Card
            hoverable={!disabled}
            style={{
                ...avenantCardStyle,
                opacity: disabled ? 0.6 : 1,
                cursor: disabled ? "not-allowed" : "pointer"
            }}
            bodyStyle={{
                padding: 0,
                height: "100%",
                display: "flex",
                flexDirection: "column"
            }}
            onClick={disabled ? undefined : onClick}
        >
            <div style={headerStyle}>
                <Space size="middle" style={{ width: "100%" }}>
                    <div style={{
                        fontSize: "32px",
                        background: "rgba(255,255,255,0.2)",
                        width: "56px",
                        height: "56px",
                        borderRadius: "12px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center"
                    }}>
                        {icon}
                    </div>
                    <Title level={5} style={{ margin: 0, color: "white", flex: 1 }}>
                        {title}
                    </Title>
                </Space>
            </div>
            <div style={{
                padding: "20px",
                flex: 1,
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between"
            }}>
                <Text style={{ color: "#666", lineHeight: "1.6", marginBottom: "16px" }}>
                    {description}
                </Text>
                {badge && (
                    <div style={{ marginTop: "12px", marginBottom: "16px" }}>
                        {badge}
                    </div>
                )}
            </div>
        </Card>
    );

    const contratEffetDansLeFutur = contrat?.dateEffet ? isAfterToday(contrat.dateEffet) : false;

    // Remise en vigueur d'un contrat ÉCHU suspendu : possible tant que la suspension dure moins
    // d'UN AN (ancre = dateEffet, posée à la date de suspension par l'avenant SP). Au-delà d'un an
    // → réservé à HCO081 (dérogation). Sert à faire réapparaître l'option RV sur un contrat échu.
    const joursDepuisEffet = contrat?.dateEffet
        ? (Date.now() - new Date(contrat.dateEffet).getTime()) / 86400000
        : Infinity;
    const suspensionMoinsDUnAn = joursDepuisEffet < 365;

    // Un avenant courant « en attente » = règlement ni réglé (pas de mode/montant payé),
    // ni annulé, hors AN (toujours considéré réglé côté Mouvements). Même logique que
    // MouvementContent. On s'appuie sur la liste `reglements` (prop) — celle de l'onglet
    // Mouvements — car `contrat.reglements` n'est pas fiablement peuplé.
    const reglementsSource = (reglements && reglements.length)
        ? reglements
        : (contrat?.reglements || []);
    const hasReglementEnAttente = reglementsSource.some(
        (r) => r && !r.annule && !r.modePaiement && !r.montantPaye && r?.avenant?.code !== "AN"
    );

    // ── Éligibilité à la prorogation (contrat fractionné, premier fractionnement) ──
    // Un contrat est réellement fractionné quand son plan de fractionnement compte
    // plus d'une échéance (nombre > 1). ATTENTION : un contrat NON fractionné a
    // `fractionnement === null`, donc `fractionnement?.nombre` vaut `undefined` — d'où
    // l'ancien test `!== 1` qui renvoyait `true` à tort et affichait le modal partout.
    const isFractionne = contrat?.fractionnement?.nombre > 1;
    // La prorogation n'est autorisée QUE lors du premier fractionnement : dès qu'une
    // prorogation (avenant « PR ») a été établie, le contrat est passé sur sa 2ᵉ période
    // (le backend applique le taux de rang 2, une seule fois). On considère donc le
    // contrat déjà prorogé si l'avenant courant est « PR » OU si un mouvement « PR »
    // non annulé figure dans son historique (couvre le cas d'un avenant postérieur au PR).
    const dejaProroge = contrat?.avenant?.code === "PR"
        || reglementsSource.some((r) => r?.avenant?.code === "PR" && !r?.annule);
    const premierFractionnement = isFractionne && !dejaProroge;
    // Prorogation disponible jusqu'à 30 jours après l'échéance (fenêtre de grâce).
    const prorogationDisponible = isWithinDaysAfter(contrat?.dateEcheance, 30);
    // Le modal / la carte de prorogation ne concernent que les contrats fractionnés
    // dont le premier fractionnement est encore en cours ET dans la fenêtre de grâce.
    const prorogationEligible = premierFractionnement && prorogationDisponible;

    const avenants = [
        {
            // Une police MONO (un seul engin) ne peut pas devenir une flotte : l'incorporation
            // n'est proposée que sur un contrat DÉJÀ flotte (≥ 2 engins). Le backend l'impose aussi.
            show: contrat?.engins?.[0]?.categorie?.codeCategorie === "M"
                && contrat?.engins?.length > 1
                && isAfterToday(contrat?.dateEcheance),
            title: "Incorporation",
            description: "Ajouter un ou plusieurs nouveaux engins à la flotte existante",
            icon: <PlusOutlined />,
            color: successColor,
            onClick: withUserCheck(() => updateIncorporationModal(true))
        },
        {
            show: contrat?.engins?.[0]?.categorie?.codeCategorie === "M" && isAfterToday(contrat?.dateEcheance) && contrat?.engins?.length > 1,
            title: "Retrait",
            description: "Retirer un engin de la flotte assurée",
            icon: <MinusOutlined />,
            color: dangerColor,
            onClick: withUserCheck(() => updateRetraitModal(true))
        },
        {
            show: isAfterToday(contrat?.dateEcheance),
            title: "Augmentation de Garantie",
            description: "Souscrire à une garantie supplémentaire non obligatoire sur le contrat en cours",
            icon: <PlusOutlined />,
            color: successColor,
            onClick: withUserCheck(() => {
                if (contrat?.suspendu) {
                    toast.error("Contrat suspendu, impossible d'augmenter la garantie");
                } else {
                    updateAugmentationModal(true);
                }
            })
        },
        {
            show: isAfterToday(contrat?.dateEcheance),
            title: "Diminution de Garantie",
            description: "Retirer des garanties supplémentaires du contrat en cours",
            icon: <MinusOutlined />,
            color: warningColor,
            onClick: withUserCheck(() => {
                if (contrat?.suspendu) {
                    toast.error("Contrat suspendu, impossible de diminuer la garantie");
                } else {
                    updateDiminutionModal(true);
                }
            })
        },
        {
            // Prorogation disponible jusqu'à 30 jours après l'échéance (grâce),
            // contrairement aux autres avenants limités à l'échéance exacte.
            // Réservée aux contrats fractionnés en premier fractionnement (voir plus haut).
            // show: prorogationEligible,
            show: true,
            title: "Prorogation",
            description: "Proroger le contrat sur la période restante",
            icon: <ClockCircleOutlined />,
            color: infoColor,
            onClick: withUserCheck(() => {
                if (contrat?.suspendu) {
                    toast.error("Contrat suspendu, impossible de proroger");
                } else {
                    updateProrogationModal(true);
                }
            })
        },
        {
            show: true,
            title: "Renouvellement",
            description: "Renouveler le contrat à échéance",
            icon: <SyncOutlined />,
            color: primaryColor,
            onClick: withUserCheck(() => {
                if (contrat?.suspendu) {
                    toast.error("Contrat suspendu, impossible de renouveler");
                    return;
                }
                // Le modal « proroger ou renouveler » ne doit apparaître QUE pour un contrat
                // fractionné dont le PREMIER fractionnement est encore en cours (mêmes
                // conditions que la carte Prorogation : voir `prorogationEligible`).
                //  - Contrat non fractionné → renouvellement direct, aucun modal.
                //  - Contrat fractionné déjà prorogé (2ᵉ période ou +) → renouvellement direct.
                //  - Contrat fractionné en premier fractionnement → on propose la prorogation,
                //    car les utilisateurs renouvelaient par erreur au lieu de proroger.
                if (prorogationEligible) {
                    Modal.confirm({
                        title: "Contrat fractionné : la prorogation est peut-être plus adaptée",
                        icon: <ClockCircleOutlined style={{ color: infoColor }} />,
                        width: 560,
                        keyboard: false, // évite que ESC déclenche onCancel et ouvre le renouvellement par erreur
                        content: (
                            <div>
                                <p>
                                    Ce contrat est <strong>fractionné</strong>. Avant de le renouveler,
                                    vérifiez qu'une <strong>prorogation</strong> ne conviendrait pas mieux.
                                </p>
                                <p style={{ marginBottom: 0 }}>
                                    La prorogation reconduit le contrat sur la période restante du
                                    fractionnement et limite le risque d'erreurs, tandis que le
                                    renouvellement établit un nouveau contrat à échéance.
                                </p>
                            </div>
                        ),
                        okText: "Proroger le contrat",
                        cancelText: "Continuer le renouvellement",
                        onOk: () => updateProrogationModal(true),
                        onCancel: () => updateRenouvellementModal(true),
                    });
                    return;
                }
                updateRenouvellementModal(true);
            }),
            badge: contrat?.dateEcheance && (
                <Space direction="vertical" size="small">
                    <Text type="secondary" style={{ fontSize: "12px" }}>
                        Doit renouveler avant le:
                    </Text>
                    <Text strong style={{ color: primaryColor }}>
                        {frenchDate(contrat?.dateEcheance)}
                    </Text>
                </Space>
            )
        },
        {
            // Contrat en cours : visible normalement. Contrat ÉCHU : visible UNIQUEMENT sous
            // HCO081 (même habilitation que l'antidatage) — la date de suspension sera alors
            // antérieure à l'échéance ; le backend exige aussi HCO081 (resolveAntidate).
            show: isAfterToday(contrat?.dateEcheance) || rightsService("HCO081"),
            title: "Suspension",
            description: "Suspendre temporairement l'effet du contrat en cours",
            icon: <PauseCircleOutlined />,
            color: warningColor,
            onClick: withUserCheck(() => updateSuspensionModal(true))
        },
        {
            // Contrat en cours : visible. Contrat ÉCHU suspendu : visible tant que la suspension
            // dure < 1 an, ou sous HCO081 au-delà (la reprise devra alors être antidatée).
            show: isAfterToday(contrat?.dateEcheance)
                || (contrat?.suspendu && (suspensionMoinsDUnAn || rightsService("HCO081"))),
            title: "Remise en Vigueur",
            description: "Remettre en vigueur un contrat suspendu",
            icon: <PlayCircleOutlined />,
            color: successColor,
            onClick: withUserCheck(() => updateRemiseEnVigueurModal(true))
        },
        {
            show: isAfterToday(contrat?.dateEcheance),
            title: "Report d'Effet",
            description: "Reporter l'effet du contrat sur une date ultérieure",
            icon: <CalendarOutlined />,
            color: infoColor,
            onClick: withUserCheck(() => {
                if (contrat?.suspendu) {
                    toast.error("Contrat suspendu, impossible de reporter l'effet");
                } else {
                    updateReportEffetModal(true);
                }
            })
        },
        {
            show: isAfterToday(contrat?.dateEcheance),
            title: "Résiliation",
            description: contratEffetDansLeFutur
                ? "La résiliation sera disponible une fois la date d'effet atteinte."
                : "Résilier définitivement le contrat en cours",
            icon: <StopOutlined />,
            color: dangerColor,
            onClick: () => updateResiliationModal(true),
            disabled: contratEffetDansLeFutur,
            badge: contratEffetDansLeFutur ? (
                <Text type="secondary" strong>
                    Disponible après la date d'effet
                </Text>
            ) : null
        },
        {
            show: isAfterToday(contrat?.dateEcheance),
            title: "Transfert de Garantie",
            description: "Transférer des garanties supplémentaires d'un engin à un autre du même assuré",
            icon: <SwapOutlined />,
            color: infoColor,
            onClick: withUserCheck(() => {
                if (contrat?.suspendu) {
                    toast.error("Contrat suspendu, impossible de transférer les garanties");
                } else {
                    updateTransfertGarantieModal(true);
                }
            })
        },
        {
            show: isAfterToday(contrat?.dateEcheance),
            title: "Changement de Véhicule",
            description: "Remplacer le véhicule assuré par un nouveau véhicule",
            icon: <SwapOutlined />,
            color: primaryColor,
            onClick: withUserCheck(() => {
                if (contrat?.suspendu) {
                    toast.error("Contrat suspendu, impossible de changer de véhicule");
                } else {
                    updateChangementVehiculeModal(true);
                }
            })
        },
        {
            show: isAfterToday(contrat?.dateEcheance),
            title: "Avenant d'Ordre",
            description: "Modifier les informations du souscripteur",
            icon: <EditOutlined />,
            color: secondaryColor,
            onClick: withUserCheck(() => updateAvenantOrdreModal(true))
        },
        {
            show: isAfterToday(contrat?.dateEcheance),
            title: "Avenant d'Ordre - Engin",
            description: "Modifier les informations de l'engin assuré",
            icon: <EditOutlined />,
            color: secondaryColor,
            onClick: withUserCheck(() => updateAvenantOrdreEnginModal(true))
        },
        {
            show: true,
            alwaysAllowed: true,
            title: "Duplicata",
            description: "Réimprimer les documents d'assurance",
            icon: <CopyOutlined />,
            color: warningColor,
            onClick: withUserCheck(() => {
                if (contrat?.suspendu) {
                    toast.error("Contrat suspendu, impossible d'effectuer le duplicata");
                } else {
                    updateDuplicataModal(true);
                }
            })
        },
        {
            // Avenant d'annulation (AA) : annule l'avenant EN COURS payé et encaissé (types annulables,
            // cf. AnnulationEnginsService.AVENANTS_ANNULABLES) → retour à l'état d'avant, ristourne
            // PN + coût de pièces + taxes sans prorata, reçu en caisse. Conditions vérifiées par le
            // backend dans la modale.
            show: contrat?.statutContrat === "VALIDE"
                && ["IN", "AG", "PR", "TG", "RV", "CV", "OE"].includes(contrat?.avenant?.code)
                && rightsService("HCO082"),
            title: "Avenant d'annulation",
            description: "Annuler l'avenant en cours : le contrat revient à son état d'avant et toute la "
                + "prime (prime nette, coût de pièces et taxes) est ristournée, sans prorata, avec reçu.",
            icon: <DeleteOutlined />,
            color: dangerColor,
            onClick: withUserCheck(() => setModalAnnulationEngins(true)),
            badge: (
                <Text type="secondary" strong>
                    Prime payée et encaissée
                </Text>
            )
        },
        {
            // Annulation DÉFINITIVE : contrat VALIDÉ dont la date d'effet est encore future
            // (après la prise d'effet → résiliation). Même droit que l'annulation d'avenant
            // (HCO082). alwaysAllowed : un avenant en attente de règlement ne bloque pas —
            // l'annulation annule justement les règlements en attente.
            show: contrat?.statutContrat === "VALIDE"
                && contratEffetDansLeFutur
                && rightsService("HCO082"),
            alwaysAllowed: true,
            title: "Annulation de Contrat",
            description: "Annuler définitivement un contrat dont la date d'effet est future : "
                + "écriture d'émission contrepassée, règlements en attente annulés, contrat figé.",
            icon: <DeleteOutlined />,
            color: dangerColor,
            onClick: withUserCheck(() => onAnnulerContrat && onAnnulerContrat()),
            badge: (
                <Text type="secondary" strong>
                    Possible tant que le contrat n'a pas pris effet
                </Text>
            )
        }
    ];

    // Avenants « autres » : regroupés sous une section dépliable en bas de la liste.
    // Opérations administratives/spécifiques que l'on consulte ponctuellement.
    const autresAvenants = [
        {
            show: isAdmin,
            alwaysAllowed: true,
            title: "Changement de Producteur",
            description: "Modifier le producteur associé au contrat (opération administrative réservée aux administrateurs, sans mouvement financier)",
            icon: <UserOutlined />,
            color: secondaryColor,
            onClick: withUserCheck(() => updateChangementProducteurModal(true)),
            badge: (
                <Text type="secondary" strong style={{ fontSize: 12 }}>
                    Réservé aux administrateurs
                </Text>
            )
        },
        {
            // Correction hors avenant d'un contrat déjà encaissé (HCO107) : la commission des
            // règlements bascule vers le nouvel intermédiaire. alwaysAllowed : opération
            // administrative, sans mouvement financier ni nouvelle version.
            show: rightsService("HCO107"),
            alwaysAllowed: true,
            title: "Changement d'intermédiaire",
            description: "Corriger l'intermédiaire d'un contrat déjà encaissé, sans avenant : la commission "
                + "des règlements est reportée sur le nouvel intermédiaire, à son taux",
            icon: <TeamOutlined />,
            color: secondaryColor,
            onClick: withUserCheck(() => setModalChangementIntermediaire(true)),
        }
    ];

    // Un contrat ANNULÉ est définitivement figé : aucune opération d'avenant n'est proposée
    // (le backend le refuse également — garde dans updateContratThroughAvenant).
    if (contrat?.statutContrat === "ANNULE") {
        return (
            <div style={{
                background: "white",
                padding: "32px",
                borderRadius: "12px",
                boxShadow: "0 2px 8px rgba(0,0,0,0.06)"
            }}>
                <Alert
                    type="error"
                    showIcon
                    style={{ borderRadius: 10 }}
                    message="Contrat annulé"
                    description="Ce contrat a été définitivement annulé : plus aucune opération
                        (avenant, renouvellement, résiliation, duplicata…) n'est possible."
                />
            </div>
        );
    }

    const renderAvenantCol = (avenant, index) => {
        const blockedByPending = hasReglementEnAttente && !avenant.alwaysAllowed;
        const effectiveDisabled = avenant.disabled || blockedByPending;
        const effectiveBadge = blockedByPending ? (
            <Text type="warning" strong style={{ fontSize: 12 }}>
                Réglez ou annulez l'avenant en cours d'abord
            </Text>
        ) : avenant.badge;
        return (
            <Col xs={24} sm={12} lg={8} key={index}>
                <AvenantCard {...avenant} disabled={effectiveDisabled} badge={effectiveBadge} />
            </Col>
        );
    };

    return (
        <div style={{
            background: "white",
            padding: "32px",
            borderRadius: "12px",
            boxShadow: "0 2px 8px rgba(0,0,0,0.06)"
        }}>
            <div style={{
                marginBottom: "32px",
                paddingBottom: "24px",
                borderBottom: `2px solid ${secondaryColor}20`
            }}>
                <Title level={3} style={{ margin: 0, color: secondaryColor }}>
                    Gestion des Avenants
                </Title>
                <Text type="secondary" style={{ fontSize: "16px" }}>
                    Sélectionnez le type d'avenant à appliquer au contrat
                </Text>
            </div>

            {hasReglementEnAttente && (
                <Alert
                    type="warning"
                    showIcon
                    style={{ marginBottom: 24, borderRadius: 10 }}
                    message="Avenant en attente de règlement"
                    description="Un avenant de ce contrat n'est ni réglé ni annulé. Réglez-le ou annulez-le avant de créer un nouvel avenant. (Le duplicata reste disponible.)"
                />
            )}

            <Row gutter={[24, 24]}>
                {avenants.filter(av => av.show).map((avenant, index) => renderAvenantCol(avenant, index))}
            </Row>

            {autresAvenants.some(av => av.show) && (
                <Collapse
                    ghost
                    style={{ marginTop: 32 }}
                    items={[
                        {
                            key: "autres-avenants",
                            label: (
                                <Text strong style={{ fontSize: 16, color: secondaryColor }}>
                                    Autres avenants
                                </Text>
                            ),
                            children: (
                                <Row gutter={[24, 24]}>
                                    {autresAvenants
                                        .filter(av => av.show)
                                        .map((avenant, index) => renderAvenantCol(avenant, index))}
                                </Row>
                            ),
                        },
                    ]}
                />
            )}

            {/* Modals */}
            <Incorporation
                open={modalIncorporation}
                update={updateIncorporationModal}
                contrat={contrat}
                onRefresh={onRefresh}
            />
            <RetraitVehicule
                open={modalRetrait}
                update={updateRetraitModal}
                contrat={contrat}
                onRefresh={onRefresh}
            />
            <AugmentationDeGarantie
                open={modalAugmentation}
                update={updateAugmentationModal}
                contrat={contrat}
                onRefresh={onRefresh}
            />
            <DiminutionDeGarantie
                open={modalDiminution}
                update={updateDiminutionModal}
                contrat={contrat}
                onRefresh={onRefresh}
            />
            <Prorogation
                open={modalProrogation}
                update={updateProrogationModal}
                contrat={contrat}
                onRefresh={onRefresh}
            />
            <SuspensionContrat
                open={modalSuspension}
                update={updateSuspensionModal}
                contrat={contrat}
                onRefresh={onRefresh}
            />
            <RemiseEnVigueur
                open={modalRemiseEnVigueur}
                update={updateRemiseEnVigueurModal}
                contrat={contrat}
                onRefresh={onRefresh}
            />
            <ReportEffet
                open={modalReportEffet}
                update={updateReportEffetModal}
                contrat={contrat}
                onRefresh={onRefresh}
            />
            <TransfertDeGaranties
                open={modalTransfertGarantie}
                update={updateTransfertGarantieModal}
                contrat={contrat}
                onRefresh={onRefresh}
            />
            <ChangementDeVehicule
                open={modalChangementVehicule}
                update={updateChangementVehiculeModal}
                contrat={contrat}
                onRefresh={onRefresh}
            />
            <Renouvellement
                open={modalRenouvellement}
                update={updateRenouvellementModal}
                contrat={contrat}
                onRefresh={onRefresh}
            />
            <AvenantDOrdre
                open={modalAvenantOrdre}
                update={updateAvenantOrdreModal}
                contrat={contrat}
                onRefresh={onRefresh}
            />
            <AvenantDOrdreEngin
                open={modalAvenantOrdreEngin}
                update={updateAvenantOrdreEnginModal}
                contrat={contrat}
                onRefresh={onRefresh}
            />
            <Duplicata
                open={modalDuplicata}
                update={updateDuplicataModal}
                contrat={contrat}
                onRefresh={onRefresh}
            />
            <Resiliation
                open={modalResiliation}
                update={updateResiliationModal}
                contrat={contrat}
                onRefresh={onRefresh}
            />
            <ChangementProducteur
                open={modalChangementProducteur}
                update={updateChangementProducteurModal}
                contrat={contrat}
                onRefresh={onRefresh}
            />
            <AnnulationEngins
                open={modalAnnulationEngins}
                update={setModalAnnulationEngins}
                contrat={contrat}
            />
            <ChangementIntermediaire
                open={modalChangementIntermediaire}
                update={setModalChangementIntermediaire}
                contrat={contrat}
                onRefresh={onRefresh}
            />
        </div>
    );
};

export default AvenantsTab;
